<template>
  <div>
    <v-app-bar flat color="red-darken-3">
      <v-app-bar-title class="hidden-sm-and-down">
        {{ $t("rr_display.title") }}
      </v-app-bar-title>
      <v-divider class="mx-5 hidden-sm-and-down" inset vertical />
    </v-app-bar>

    <v-container fluid class="pa-5">
      <v-snackbar
        v-model="snackbar"
        :color="snackbarColor"
        location="top"
        timeout="4000"
      >
        {{ snackbarMessage }}
      </v-snackbar>

      <v-row v-if="loading" align="center" justify="center" class="ma-8">
        <v-progress-circular :size="64" color="red-darken-3" indeterminate />
      </v-row>

      <v-card v-else flat rounded="lg" border class="pa-5" max-width="720">
        <div class="d-flex align-center mb-4">
          <img
            height="32"
            width="32"
            class="mr-3"
            src="@/assets/rr-logo.png"
            alt="ReteRicambi"
          />
          <h2 class="text-h6 font-weight-medium mb-0">
            {{ $t("rr_display.heading") }}
          </h2>
        </div>

        <p class="text-body-2 text-medium-emphasis mb-6">
          {{ $t("rr_display.description") }}
        </p>

        <v-alert
          v-if="!institutionId"
          type="info"
          variant="tonal"
          class="mb-4"
          density="comfortable"
        >
          {{ $t("rr_display.no_institution_hint") }}
        </v-alert>

        <template v-else>
          <v-alert
            v-if="!canEdit"
            type="info"
            variant="tonal"
            class="mb-4"
            density="comfortable"
          >
            {{ $t("rr_display.read_only_hint") }}
          </v-alert>

          <v-alert
            v-else-if="currentRr"
            type="success"
            variant="tonal"
            class="mb-4"
            density="comfortable"
          >
            {{ $t("rr_display.status_online") }}
          </v-alert>

          <v-alert
            v-else-if="canEdit"
            type="warning"
            variant="tonal"
            class="mb-4"
            density="comfortable"
          >
            {{ $t("rr_display.status_offline") }}
          </v-alert>

          <v-switch
            v-if="canEdit"
            v-model="rr"
            :label="$t('rr_display.toggle_label')"
            color="red-darken-3"
            hide-details
            class="mb-4"
          />

          <v-alert
            v-if="canEdit"
            type="info"
            variant="tonal"
            class="mb-6"
            density="comfortable"
          >
            <p class="text-subtitle-2 font-weight-medium mb-2">
              {{ $t("rr_display.info_title") }}
            </p>
            <ul class="text-body-2 mb-0 pl-4">
              <li class="mb-2">{{ $t("rr_display.info_toggle_only") }}</li>
              <li>{{ $t("rr_display.info_scheduled_pause") }}</li>
            </ul>
          </v-alert>

          <!-- Scheduled pause (optional) -->
          <v-divider v-if="canEdit" class="mb-4" />

          <div v-if="canEdit" class="mb-2">
            <p class="text-subtitle-2 font-weight-medium mb-1">
              {{ $t("rr_display.pause_heading") }}
            </p>
            <p class="text-body-2 text-medium-emphasis mb-4">
              {{ $t("rr_display.pause_description") }}
            </p>

            <v-row dense>
              <v-col cols="12" sm="6">
                <v-text-field
                  v-model="pauseFrom"
                  :label="$t('rr_display.pause_from')"
                  type="date"
                  color="red-darken-3"
                  variant="outlined"
                  density="comfortable"
                  clearable
                  hide-details="auto"
                  :error-messages="pauseError"
                />
              </v-col>
              <v-col cols="12" sm="6">
                <v-text-field
                  v-model="pauseTo"
                  :label="$t('rr_display.pause_to')"
                  type="date"
                  color="red-darken-3"
                  variant="outlined"
                  density="comfortable"
                  clearable
                  hide-details="auto"
                  :min="pauseFrom || undefined"
                />
              </v-col>
            </v-row>

            <v-alert
              v-if="isPausedNow"
              type="info"
              variant="tonal"
              class="mt-3"
              density="comfortable"
            >
              {{ $t("rr_display.pause_active", { from: pauseFrom, to: pauseTo }) }}
            </v-alert>
          </div>

          <v-btn
            v-if="canEdit"
            color="success"
            variant="flat"
            :loading="saving"
            :disabled="!hasChanges || !!pauseError"
            class="mt-4"
            @click="save"
          >
            {{ $t("rr_display.save") }}
          </v-btn>
        </template>
      </v-card>
    </v-container>
  </div>
</template>

<script>
import getRequest from "@/api/getRequest";
import putRequest from "@/api/putRequest";

export default {
  name: "ReteRicambiDisplay",
  data() {
    return {
      loading: true,
      saving: false,
      rr: false,
      savedRr: false,
      pauseFrom: null,
      pauseTo: null,
      savedPauseFrom: null,
      savedPauseTo: null,
      snackbar: false,
      snackbarMessage: "",
      snackbarColor: "success",
    };
  },
  computed: {
    canEdit() {
      return this.$store.getters.role === "InstitutionAdmin";
    },
    hasChanges() {
      return (
        this.rr !== this.savedRr ||
        this.pauseFrom !== this.savedPauseFrom ||
        this.pauseTo !== this.savedPauseTo
      );
    },
    institutionId() {
      return this.$store.getters.institutionId;
    },
    currentRr() {
      return this.canEdit ? this.rr : this.savedRr;
    },
    pauseError() {
      if (this.pauseFrom && this.pauseTo && this.pauseFrom >= this.pauseTo) {
        return this.$t("rr_display.pause_error_range");
      }
      if ((this.pauseFrom && !this.pauseTo) || (!this.pauseFrom && this.pauseTo)) {
        return this.$t("rr_display.pause_error_both");
      }
      return "";
    },
    isPausedNow() {
      if (!this.pauseFrom || !this.pauseTo) return false;
      const now = new Date().toISOString().slice(0, 10);
      return now >= this.pauseFrom && now <= this.pauseTo;
    },
  },
  beforeMount() {
    this.load();
  },
  methods: {
    toDateString(val) {
      if (!val) return null;
      const d = new Date(val);
      if (Number.isNaN(d.getTime())) return null;
      return d.toISOString().slice(0, 10);
    },
    load() {
      if (!this.institutionId) {
        this.loading = false;
        return;
      }

      this.loading = true;
      getRequest("/institution", `id=${this.institutionId}`)
        .then((response) => {
          const institution = response.institution && response.institution[0];
          if (!institution) return;
          this.rr = institution.rr === true;
          this.savedRr = institution.rr === true;
          this.pauseFrom = this.toDateString(institution.rrPause?.from);
          this.pauseTo = this.toDateString(institution.rrPause?.to);
          this.savedPauseFrom = this.pauseFrom;
          this.savedPauseTo = this.pauseTo;
        })
        .catch(() => {
          this.showSnackbar(this.$t("rr_display.load_error"), "error");
        })
        .finally(() => {
          this.loading = false;
        });
    },
    save() {
      this.saving = true;
      const payload = {
        rr: this.rr,
        rrPause: {
          from: this.pauseFrom || null,
          to: this.pauseTo || null,
        },
      };
      putRequest("/institution/rr-display", "", payload)
        .then(() => {
          this.savedRr = this.rr;
          this.savedPauseFrom = this.pauseFrom;
          this.savedPauseTo = this.pauseTo;
          this.showSnackbar(this.$t("rr_display.save_success"), "success");
        })
        .catch(() => {
          this.showSnackbar(this.$t("rr_display.save_error"), "error");
        })
        .finally(() => {
          this.saving = false;
        });
    },
    showSnackbar(message, color) {
      this.snackbarMessage = message;
      this.snackbarColor = color;
      this.snackbar = true;
    },
  },
};
</script>
